import React, { useState, useEffect, useCallback } from 'react';
import { UserPlus, Send, CheckCircle, Ban, Pencil, Upload } from 'lucide-react';
import api from '../../services/api';
import JoinerImport from './JoinerImport';
import { useDialogs } from '../../components/Dialogs';
import { field, ghost, primary, muted, card, rowBorder, errorText } from '../time/styles';

// HR → Joiners & leavers → New joiners: people who haven't joined yet. HR
// adds them (they get an invite to the joining portal), edits details,
// converts them on joining day (it also happens automatically), or withdraws.

const TYPES = { FULL_TIME: 'Full-time', CONTRACTOR: 'Contractor', PARTNER: 'Partner', INTERN: 'Intern' };
const INVITE = { NONE: 'No invite sent', SENT: 'Invite sent', EXPIRED: 'Invite expired', REVOKED: 'Invite replaced', ACCEPTED: 'Signed up' };
const fmt = (key) => (key ? new Date(`${key}T00:00:00Z`).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }) : '');

// A labelled field (module level, so inputs keep focus while typing).
const L = ({ label, children, grow }) => <label style={{ display: 'grid', gap: 4, flex: grow ? '1 1 220px' : undefined }}><span style={muted}>{label}</span>{children}</label>;

const JoinerForm = ({ initial, users, departments, checklists, onSaved, onCancel, say }) => {
    const editing = !!initial?.id;
    const [f, setF] = useState(() => initial || { name: '', personalEmail: '', joiningDate: '', designation: '', departmentId: '', managerId: '', employmentType: 'FULL_TIME', role: 'USER', salary: '', officeEmail: '', checklistTemplateId: '', buddyId: '', sendInvite: true });
    const [busy, setBusy] = useState(false);
    const set = (k, v) => setF(p => ({ ...p, [k]: v }));
    const active = users.filter(u => u.isActive !== false);
    useEffect(() => {
        if (!editing && !f.checklistTemplateId && checklists.length === 1) set('checklistTemplateId', checklists[0].id);
    }, [editing, checklists, f.checklistTemplateId]);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const body = { ...f, departmentId: f.departmentId || null, managerId: f.managerId || null, buddyId: f.buddyId || null, checklistTemplateId: f.checklistTemplateId || null, officeEmail: f.officeEmail || null, salary: f.salary === '' ? undefined : f.salary };
            if (editing) {
                const r = await api.patch(`/lifecycle/joiners/${initial.id}`, body);
                say(true, r.data.moved ? `Saved. Their checklist dates moved by ${r.data.moved} days.` : 'Saved');
            } else {
                const r = await api.post('/lifecycle/joiners', body);
                say(true, `Added ${f.name}.${r.data.inviteExpiresAt ? ` An invite was emailed to ${f.personalEmail}.` : ''}`);
            }
            onSaved();
        } catch (err) {
            say(false, errorText(err, 'Could not save'));
        } finally {
            setBusy(false);
        }
    };
    return (
        <form onSubmit={submit} style={{ display: 'grid', gap: 12 }}>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <L label="Full name" grow><input aria-label="Full name" required maxLength={120} style={field} value={f.name} onChange={e => set('name', e.target.value)} /></L>
                <L label="Personal email (the invite goes here; they sign in with it until they join)" grow>
                    <input aria-label="Personal email" type="email" required style={field} value={f.personalEmail} disabled={editing} onChange={e => set('personalEmail', e.target.value)} />
                </L>
                <L label="Joining date"><input aria-label="Joining date" type="date" required style={field} value={f.joiningDate} onChange={e => set('joiningDate', e.target.value)} /></L>
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <L label="Designation"><input aria-label="Designation" maxLength={100} style={field} value={f.designation || ''} onChange={e => set('designation', e.target.value)} /></L>
                <L label="Department">
                    <select aria-label="Department" style={field} value={f.departmentId || ''} onChange={e => set('departmentId', e.target.value)}>
                        <option value="">None</option>
                        {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                </L>
                <L label="Reporting manager">
                    <select aria-label="Reporting manager" style={field} value={f.managerId || ''} onChange={e => set('managerId', e.target.value)}>
                        <option value="">None</option>
                        {active.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                    </select>
                </L>
                <L label="Employment type">
                    <select aria-label="Employment type" style={field} value={f.employmentType} onChange={e => set('employmentType', e.target.value)}>
                        {Object.entries(TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                </L>
                <L label="Role">
                    <select aria-label="Role" style={field} value={f.role} onChange={e => set('role', e.target.value)}>
                        <option value="USER">Employee</option><option value="MANAGER">Manager</option><option value="FINANCE">Finance</option><option value="HR">HR</option>
                    </select>
                </L>
                <L label="Monthly salary"><input aria-label="Monthly salary" type="number" min="0" style={{ ...field, width: 140 }} value={f.salary ?? ''} onChange={e => set('salary', e.target.value)} /></L>
                <L label="Work email (optional, can be added later)"><input aria-label="Work email" type="email" style={field} value={f.officeEmail || ''} onChange={e => set('officeEmail', e.target.value)} /></L>
            </div>
            {!editing && (
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <L label="Onboarding checklist">
                        <select aria-label="Onboarding checklist" style={field} value={f.checklistTemplateId} onChange={e => set('checklistTemplateId', e.target.value)}>
                            <option value="">None for now</option>
                            {checklists.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                    </L>
                    <L label="Buddy (optional)">
                        <select aria-label="Buddy" style={field} value={f.buddyId} onChange={e => set('buddyId', e.target.value)}>
                            <option value="">None</option>
                            {active.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                        </select>
                    </L>
                    <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}><input type="checkbox" checked={f.sendInvite} onChange={e => set('sendInvite', e.target.checked)} />Email the invite now</label>
                </div>
            )}
            <p style={{ ...muted, margin: 0 }}>They don't use a seat until they join. On the joining day their account becomes a full employee account{f.officeEmail ? ' and they sign in with the work email' : ''}.</p>
            <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary" style={primary} disabled={busy}><UserPlus size={14} /> {busy ? 'Saving…' : editing ? 'Save' : 'Add new joiner'}</button>
                <button type="button" style={ghost} onClick={onCancel}>Cancel</button>
            </div>
        </form>
    );
};

const JoinersPanel = ({ users, say, onChanged }) => {
    const [data, setData] = useState(null);
    const [departments, setDepartments] = useState([]);
    const [checklists, setChecklists] = useState([]);
    const [form, setForm] = useState(null); // null | 'new' | 'import' | joiner
    const { ask, dialogs } = useDialogs();
    const load = useCallback(() => api.get('/lifecycle/joiners').then(r => setData(r.data)).catch(e => say(false, errorText(e, 'Could not load new joiners'))), [say]);
    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        api.get('/departments').then(r => setDepartments(r.data)).catch(() => {});
        api.get('/lifecycle/checklists').then(r => setChecklists(r.data.templates.filter(t => t.isActive && t.kind === 'ONBOARDING'))).catch(() => {});
    }, []);
    const act = async (fn, ok) => {
        try { const r = await fn(); say(true, typeof ok === 'function' ? ok(r.data) : ok); load(); onChanged(); } catch (e) { say(false, errorText(e, 'That did not work')); }
    };
    const convert = async (j) => {
        const early = j.joiningDate > data.today ? `${j.name} joins on ${fmt(j.joiningDate)}. Converting now uses a seat from today.\n\n` : '';
        const email = await ask({ title: `Make ${j.name} a full employee`, message: `${early}They sign in with their work email from now on. Leave it empty to keep signing in with ${j.email}.`, label: 'Work email', type: 'email', defaultValue: j.officeEmail || '', confirmText: 'Convert' });
        if (email === null) return;
        const work = email.trim();
        act(() => api.post(`/lifecycle/joiners/${j.id}/convert`, work ? { officeEmail: work } : { loginWithWorkEmail: false }), r => `${j.name} is now an employee (${r.employeeId}), signing in with ${r.email}.`);
    };
    const withdraw = async (j) => {
        const reason = await ask({ title: `${j.name} is not joining?`, message: 'Their portal and checklist close.', required: true, confirmText: 'Withdraw' });
        if (reason === null) return;
        act(() => api.post(`/lifecycle/joiners/${j.id}/withdraw`, { reason }), `${j.name} withdrawn`);
    };
    const done = () => { setForm(null); load(); onChanged(); };

    return (
        <section className="card" style={card} aria-label="New joiners">
            {dialogs}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <h2 style={{ fontSize: 18, margin: 0 }}>New joiners</h2>
                {!form && <div style={{ display: 'flex', gap: 8 }}><button style={ghost} onClick={() => setForm('import')}><Upload size={14} /> Import</button><button className="btn btn-primary" style={primary} onClick={() => setForm('new')}><UserPlus size={14} /> New joiner</button></div>}
            </div>
            {form && (
                <div style={{ borderTop: rowBorder, marginTop: 12, paddingTop: 12 }}>
                    {form === 'import' ? <JoinerImport checklists={checklists} say={say} onDone={() => { load(); onChanged(); }} onCancel={() => setForm(null)} /> : <JoinerForm key={form === 'new' ? 'new' : form.id} initial={form === 'new' ? null : form} users={users} departments={departments} checklists={checklists} say={say} onSaved={done} onCancel={() => setForm(null)} />}
                </div>
            )}
            {!data ? <p style={muted}>Loading…</p> : data.joiners.length === 0 ? <p style={{ ...muted, marginBottom: 0 }}>Nobody is waiting to join.</p> : (
                <ul style={{ listStyle: 'none', margin: '8px 0 0', padding: 0 }}>
                    {data.joiners.map(j => (
                        <li key={j.id} data-testid={`joiner-${j.name}`} style={{ borderTop: rowBorder, padding: '10px 0', display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                            <div>
                                <strong style={{ color: 'var(--text-main)' }}>{j.name}</strong> <span style={muted}>{j.designation ? `· ${j.designation}` : ''}{j.managerName ? ` · reports to ${j.managerName}` : ''}</span>
                                <div style={muted}>
                                    Joins {fmt(j.joiningDate)}{j.joiningDate <= data.today ? ' (today or earlier)' : ''} · {INVITE[j.invite]} · {j.email}
                                    {j.progress ? ` · checklist ${j.progress.done}/${j.progress.total}` : ''}
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                {j.invite !== 'ACCEPTED' && <button style={ghost} onClick={() => act(() => api.post(`/lifecycle/joiners/${j.id}/invite`), `New invite emailed to ${j.email}`)}><Send size={14} /> {j.invite === 'NONE' ? 'Send invite' : 'Resend invite'}</button>}
                                <button style={ghost} aria-label={`Edit ${j.name}`} onClick={() => setForm({ ...j, personalEmail: j.email, salary: j.salary || '', departmentId: j.departmentId || '', managerId: j.managerId || '', officeEmail: j.officeEmail || '' })}><Pencil size={14} /></button>
                                <button style={ghost} onClick={() => convert(j)} aria-label={`Convert ${j.name} to an employee`}><CheckCircle size={14} /> {j.joiningDate > data.today ? 'Convert early' : 'Has joined'}</button>
                                <button style={ghost} onClick={() => withdraw(j)} aria-label={`Withdraw ${j.name}`}><Ban size={14} /></button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
};

export default JoinersPanel;
